import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Button } from "@rakazo/ui-web";
import { useEffect, useRef, useState } from "react";

const SIZE = 240;
export function AvatarPhotoCrop({
  file,
  onUse,
  onChoose,
}: {
  file: File;
  onUse: (value: string) => void;
  onChoose: () => void;
}) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [error, setError] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  useEffect(() => {
    let active = true;
    const url = URL.createObjectURL(file),
      next = new Image();
    setImage(null);
    setError(false);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    next.onload = () => {
      if (active) setImage(next);
    };
    next.onerror = () => {
      if (active) setError(true);
    };
    next.src = url;
    return () => {
      active = false;
      URL.revokeObjectURL(url);
    };
  }, [file]);
  const scale = image ? Math.max(SIZE / image.width, SIZE / image.height) * zoom : 1;
  const maxX = image ? (image.width * scale - SIZE) / 2 : 0,
    maxY = image ? (image.height * scale - SIZE) / 2 : 0;
  const x = Math.max(-maxX, Math.min(maxX, position.x)),
    y = Math.max(-maxY, Math.min(maxY, position.y));
  function save() {
    if (!image) return;
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const crop = SIZE / scale;
    ctx.drawImage(
      image,
      (image.width - crop) / 2 - x / scale,
      (image.height - crop) / 2 - y / scale,
      crop,
      crop,
      0,
      0,
      256,
      256,
    );
    onUse(canvas.toDataURL("image/webp", 0.9));
  }
  return (
    <div className="flex flex-col items-center gap-5">
      <div
        role="img"
        aria-label={t`Photo crop preview`}
        className="relative touch-none cursor-move overflow-hidden rounded-full border-2 border-foreground bg-muted"
        style={{ width: SIZE, height: SIZE }}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          drag.current = { x: e.clientX, y: e.clientY, px: x, py: y };
        }}
        onPointerMove={(e) => {
          if (drag.current)
            setPosition({
              x: Math.max(-maxX, Math.min(maxX, drag.current.px + e.clientX - drag.current.x)),
              y: Math.max(-maxY, Math.min(maxY, drag.current.py + e.clientY - drag.current.y)),
            });
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        onPointerCancel={() => {
          drag.current = null;
        }}
      >
        {image ? (
          <img
            src={image.src}
            alt=""
            draggable={false}
            className="pointer-events-none absolute max-w-none"
            style={{
              width: image.width * scale,
              height: image.height * scale,
              left: (SIZE - image.width * scale) / 2 + x,
              top: (SIZE - image.height * scale) / 2 + y,
            }}
          />
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          <Trans>Could not open that image.</Trans>
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground">
        <Trans>Drag to reposition</Trans>
      </p>
      <label className="flex w-full items-center gap-3 text-sm">
        <Trans>Zoom</Trans>
        <input
          aria-label={t`Zoom photo`}
          className="min-w-0 flex-1 accent-foreground"
          type="range"
          min={1}
          max={3}
          step={0.01}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
        />
      </label>
      <Button variant="secondary" className="rounded-full" onClick={onChoose}>
        <Trans>Choose another photo</Trans>
      </Button>
      <Button className="w-full rounded-full" disabled={!image} onClick={save}>
        <Trans>Use photo</Trans>
      </Button>
    </div>
  );
}
